// Enhanced Quick Notes Data with Code Examples and Rich Content

export const quickNotes = [
  {
    id: "aws-essentials",
    title: "AWS Essentials",
    icon: "☁️",
    category: "Cloud Platform",
    tags: ["AWS", "Cloud", "Infrastructure"],
    summary: "Core AWS services for cloud infrastructure: EC2 for compute, S3 for storage, Lambda for serverless, IAM for security, VPC for networking, and CloudWatch for monitoring.",
    description: "Amazon Web Services (AWS) provides a comprehensive cloud platform with services for compute, storage, databases, networking, and more. Understanding these core services is essential for cloud engineering.",
    keyPoints: [
      "EC2: Scalable virtual servers in the cloud",
      "S3: Object storage with 99.999999999% durability",
      "Lambda: Run code without managing servers",
      "IAM: Manage access and permissions securely",
      "VPC: Isolated network environments",
      "CloudWatch: Monitor resources and applications"
    ],
    codeExample: {
      language: "bash",
      title: "AWS CLI - Common Commands",
      code: `# List all EC2 instances
aws ec2 describe-instances

# Create an S3 bucket
aws s3 mb s3://my-bucket-name

# Upload file to S3
aws s3 cp file.txt s3://my-bucket-name/

# List Lambda functions
aws lambda list-functions`
    },
    slug: "/notes/aws-essentials"
  },
  {
    id: "docker-quick-reference",
    title: "Docker Quick Reference",
    icon: "🐳",
    category: "Containerization",
    tags: ["Docker", "Containers", "DevOps"],
    summary: "Essential Docker commands and concepts: building images, running containers, managing volumes, networking, and pushing to registries.",
    description: "Docker enables you to package applications with their dependencies into containers, ensuring consistency across development, testing, and production environments.",
    keyPoints: [
      "Images: Read-only templates for containers",
      "Containers: Running instances of images",
      "Dockerfile: Instructions to build images",
      "Volumes: Persistent data storage",
      "Networks: Container communication",
      "Docker Hub: Public registry for images"
    ],
    codeExample: {
      language: "bash",
      title: "Docker - Essential Commands",
      code: `# Build an image from Dockerfile
docker build -t myapp:latest .

# Run a container
docker run -d -p 8080:80 --name myapp myapp:latest

# List running containers
docker ps

# View logs
docker logs myapp

# Stop and remove container
docker stop myapp && docker rm myapp

# Push to Docker Hub
docker push username/myapp:latest`
    },
    slug: "/notes/docker-quick-reference"
  },
  {
    id: "kubernetes-core-concepts",
    title: "Kubernetes Core Concepts",
    icon: "☸️",
    category: "Orchestration",
    tags: ["Kubernetes", "K8s", "Container Orchestration"],
    summary: "Kubernetes fundamentals: Pods, Deployments, Services, ConfigMaps, Secrets, Namespaces, and essential kubectl commands for container orchestration.",
    description: "Kubernetes (K8s) is an open-source container orchestration platform that automates deployment, scaling, and management of containerized applications.",
    keyPoints: [
      "Pods: Smallest deployable units",
      "Deployments: Manage replica sets and updates",
      "Services: Expose pods to network traffic",
      "ConfigMaps: Store configuration data",
      "Secrets: Store sensitive information",
      "Namespaces: Virtual clusters for isolation"
    ],
    codeExample: {
      language: "bash",
      title: "kubectl - Common Commands",
      code: `# Get all pods
kubectl get pods

# Create deployment
kubectl create deployment nginx --image=nginx

# Expose deployment as service
kubectl expose deployment nginx --port=80 --type=LoadBalancer

# Scale deployment
kubectl scale deployment nginx --replicas=3

# View logs
kubectl logs pod-name

# Apply configuration
kubectl apply -f deployment.yaml`
    },
    slug: "/notes/kubernetes-core-concepts"
  },
  {
    id: "terraform-workflow",
    title: "Terraform Workflow",
    icon: "🧱",
    category: "Infrastructure as Code",
    tags: ["Terraform", "IaC", "Automation"],
    summary: "Terraform basics: providers, state management, init/plan/apply workflow, modules, variables, outputs, and remote state configuration.",
    description: "Terraform is an Infrastructure as Code (IaC) tool that allows you to define and provision infrastructure using declarative configuration files.",
    keyPoints: [
      "Providers: Connect to cloud platforms",
      "State: Track infrastructure resources",
      "Init: Initialize working directory",
      "Plan: Preview infrastructure changes",
      "Apply: Create/update infrastructure",
      "Modules: Reusable infrastructure components"
    ],
    codeExample: {
      language: "hcl",
      title: "Terraform - Basic Workflow",
      code: `# Initialize Terraform
terraform init

# Format configuration files
terraform fmt

# Validate configuration
terraform validate

# Plan changes
terraform plan

# Apply changes
terraform apply

# Destroy infrastructure
terraform destroy

# Show current state
terraform show`
    },
    slug: "/notes/terraform-workflow"
  },
  {
    id: "cicd-pipeline-basics",
    title: "CI/CD Pipeline Basics",
    icon: "🔁",
    category: "DevOps Practices",
    tags: ["CI/CD", "GitHub Actions", "Automation"],
    summary: "CI/CD fundamentals: build, test, and deploy stages, GitHub Actions workflows, triggers, artifacts, and rollback strategies for automated deployments.",
    description: "Continuous Integration and Continuous Deployment (CI/CD) automates the software delivery process, enabling faster and more reliable releases.",
    keyPoints: [
      "Build: Compile and package code",
      "Test: Run automated tests",
      "Deploy: Release to environments",
      "Triggers: Events that start pipelines",
      "Artifacts: Build outputs and dependencies",
      "Rollback: Revert to previous versions"
    ],
    codeExample: {
      language: "yaml",
      title: "GitHub Actions - Simple CI/CD",
      code: `name: CI/CD Pipeline

on:
  push:
    branches: [ main ]

jobs:
  build:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      
      - name: Build
        run: npm install && npm run build
      
      - name: Test
        run: npm test
      
      - name: Deploy
        run: npm run deploy`
    },
    slug: "/notes/cicd-pipeline-basics"
  },
  {
    id: "linux-commands",
    title: "Linux Commands",
    icon: "🐧",
    category: "Operating System",
    tags: ["Linux", "CLI", "System Administration"],
    summary: "Essential Linux commands: file operations, permissions management, networking tools, and system monitoring for effective server administration.",
    description: "Linux command-line proficiency is crucial for DevOps engineers. Master these commands for file management, networking, and system monitoring.",
    keyPoints: [
      "File Operations: ls, cd, cp, mv, rm, mkdir",
      "Permissions: chmod, chown, umask",
      "Networking: curl, wget, ping, netstat, ss",
      "Monitoring: top, htop, ps, df, du, free",
      "Text Processing: grep, sed, awk, cat",
      "Package Management: apt, yum, dnf"
    ],
    codeExample: {
      language: "bash",
      title: "Linux - Common Commands",
      code: `# File operations
ls -la                    # List files with details
cd /path/to/directory     # Change directory
cp source.txt dest.txt    # Copy file
mv old.txt new.txt        # Move/rename file

# Permissions
chmod 755 script.sh       # Set permissions
chown user:group file.txt # Change ownership

# Networking
curl https://api.example.com  # Make HTTP request
ping google.com               # Test connectivity
netstat -tuln                 # Show listening ports

# System monitoring
top                       # Process monitor
df -h                     # Disk usage
free -h                   # Memory usage`
    },
    slug: "/notes/linux-commands",
    notesLink: "https://drive.google.com/file/d/18fM15-m2qw2r92pPqHuOshupAtyb8gcS/view?usp=sharing",
    hasDetailedNotes: true
  }
];
